import React, { useState, useEffect } from 'react';
import Header from '../component/Header';
import BackgroundImage from '../images/tirvia/trivia-bg.png';
import { AiOutlineClockCircle } from 'react-icons/ai';
import { FaCheckCircle } from "react-icons/fa";
import { IoClose } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { getToken } from '../utils/tokenHelper';

const SymptomSprint = () => {
    const [showModal, setShowModal] = useState(true);
    const [quizStarted, setQuizStarted] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(0); 
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [timeLeft, setTimeLeft] = useState(45); // Changed from 120 to 45 seconds
    const [attemptsLeft, setAttemptsLeft] = useState(4); // Default, will be updated from API
    const [loading, setLoading] = useState(true);
    const [canAccess, setCanAccess] = useState(false);
    const [accessMessage, setAccessMessage] = useState('');
    const [quizConfig, setQuizConfig] = useState(null);
    const [batchId, setBatchId] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [startingQuiz, setStartingQuiz] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [submittingAnswer, setSubmittingAnswer] = useState(false);
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const [completionMessage, setCompletionMessage] = useState('');
    const [showTimeoutModal, setShowTimeoutModal] = useState(false);
    const [timeExpired, setTimeExpired] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const navigate = useNavigate();

    // Fetch quiz config on component mount with delay
    useEffect(() => {
        const fetchQuizConfig = async () => {
            // Add 5-second delay before fetching
            await new Promise(resolve => setTimeout(resolve, 5000));
            
            const token = getToken();
            if (!token) {
                console.error('No token found');
                setErrorMessage('Please login to play this game');
                setShowErrorModal(true);
                setTimeout(() => {
                    navigate('/trivia-games');
                }, 3000);
                setLoading(false);
                return;
            }

            const myHeaders = new Headers();
            myHeaders.append("Accept", "application/json");
            myHeaders.append("Authorization", `Bearer ${token}`);

            const requestOptions = {
                method: "GET",
                headers: myHeaders,
                redirect: "follow"
            };

            try {
                const response = await fetch("https://devapi.quickcare.ng/api/v1/games/mcq-like/config?game=symptom_sprint", requestOptions);
                
                if (!response.ok) {
                    throw new Error(`Server error: ${response.status}`);
                }
                
                const result = await response.json();
                console.log('Quiz config response:', result);
                
                if (result.success && result.data) {
                    setQuizConfig(result.data);
                    setCanAccess(result.data.can_access || false);
                    setAccessMessage(result.data.message || '');
                    
                    // Update attempts from server response
                    if (result.data.attempts !== undefined) {
                        setAttemptsLeft(result.data.attempts);
                    }
                    
                    // Update timer from max_winning_minutes (convert to seconds)
                    // Set to 45 seconds regardless of API response
                    setTimeLeft(45);
                    
                    // Store total questions
                    if (result.data.max_question !== undefined) {
                        setTotalQuestions(result.data.max_question);
                    }
                } else {
                    throw new Error(result.message || 'Failed to load game configuration');
                }
            } catch (error) {
                console.error('Error fetching quiz config:', error);
                setErrorMessage(error.message || 'Failed to load game. Please try again later.');
                setShowErrorModal(true);
                setTimeout(() => {
                    navigate('/trivia-games');
                }, 3000);
            } finally {
                setLoading(false);
            }
        };

        fetchQuizConfig();
    }, [navigate]);

    // Timer countdown - only start when quiz has started
    useEffect(() => {
        if (quizStarted && timeLeft > 0 && !timeExpired) {
            const timer = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 1) {
                        // Time expired
                        setTimeExpired(true);
                        setShowTimeoutModal(true);
                        setQuizStarted(false);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [quizStarted, timeLeft, timeExpired]);

    // Format time as MM:SS
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const handleAnswerSelect = (optionId) => {
        setSelectedAnswer(optionId);
    };

    const handleNext = async () => {
        if (!selectedAnswer || !batchId || submittingAnswer) {
            return;
        }

        setSubmittingAnswer(true);

        try {
            const token = getToken();
            
            if (!token) {
                console.error('No token found');
                setSubmittingAnswer(false);
                return;
            }

            // Convert selected answer (A, B, C, D) to lowercase (a, b, c, d)
            const answerLetter = selectedAnswer.toLowerCase();

            const myHeaders = new Headers();
            myHeaders.append("Content-Type", "application/json");
            myHeaders.append("Accept", "application/json");
            myHeaders.append("Authorization", `Bearer ${token}`);

            const raw = JSON.stringify({
                "batch_id": batchId,
                "answer": answerLetter
            });

            const requestOptions = {
                method: "POST",
                headers: myHeaders,
                body: raw,
                redirect: "follow"
            };

            const response = await fetch("https://devapi.quickcare.ng/api/v1/games/mcq-like/answer/symptom_sprint", requestOptions);
            const result = await response.json();
            console.log('Answer submission response:', result);

            if (result.success && result.data) {
                // Update current index
                setCurrentIndex(result.data.current_index);
                
                // Parse question_answers to create question object
                const questionAnswers = result.data.question_answers;
                const questionObj = {
                    id: result.data.current_index + 1,
                    question: questionAnswers.question,
                    options: [
                        { id: 'A', text: questionAnswers.a },
                        { id: 'B', text: questionAnswers.b },
                        { id: 'C', text: questionAnswers.c },
                        { id: 'D', text: questionAnswers.d }
                    ]
                };
                
                // Add new question to questions array
                setQuestions(prev => [...prev, questionObj]);
                setCurrentQuestion(result.data.current_index);
                setSelectedAnswer(null);
            }
        } catch (error) {
            console.error('Error submitting answer:', error);
        } finally {
            setSubmittingAnswer(false);
        }
    };

    const handleFinish = async () => {
        if (!selectedAnswer || !batchId || submittingAnswer) {
            return;
        }

        setSubmittingAnswer(true);

        try {
            const token = getToken();
            
            if (!token) {
                console.error('No token found');
                setSubmittingAnswer(false);
                return;
            }

            // Convert selected answer (A, B, C, D) to lowercase (a, b, c, d)
            const answerLetter = selectedAnswer.toLowerCase();

            const myHeaders = new Headers();
            myHeaders.append("Content-Type", "application/json");
            myHeaders.append("Accept", "application/json");
            myHeaders.append("Authorization", `Bearer ${token}`);

            const raw = JSON.stringify({
                "batch_id": batchId,
                "answer": answerLetter
            });

            const requestOptions = {
                method: "POST",
                headers: myHeaders,
                body: raw,
                redirect: "follow"
            };

            const response = await fetch("https://devapi.quickcare.ng/api/v1/games/mcq-like/answer/symptom_sprint", requestOptions);
            const result = await response.json();
            console.log('Final answer submission response:', result);

            if (result.success) {
                setCompletionMessage(result.message || 'Quiz completed successfully!');
                setShowCompletionModal(true);
            }
        } catch (error) {
            console.error('Error submitting final answer:', error);
        } finally {
            setSubmittingAnswer(false);
        }
    };

    const handlePlay = async () => {
        if (!canAccess) {
            return;
        }
        
        setStartingQuiz(true);
        
        try {
            const token = getToken();
            
            if (!token) {
                console.error('No token found');
                setStartingQuiz(false);
                return;
            }

            const myHeaders = new Headers();
            myHeaders.append("Accept", "application/json");
            myHeaders.append("Authorization", `Bearer ${token}`);

            const requestOptions = {
                method: "POST",
                headers: myHeaders,
                redirect: "follow"
            };

            const response = await fetch("https://devapi.quickcare.ng/api/v1/games/mcq-like/start?game=symptom_sprint", requestOptions);
            const result = await response.json();
            console.log('Start quiz response:', result);

            if (result.success && result.data) {
                // Store batch_id
                setBatchId(result.data.batch_id);
                
                // Parse question_answers to create question object
                const questionAnswers = result.data.question_answers;
                const questionObj = {
                    id: result.data.current_index + 1,
                    question: questionAnswers.question,
                    options: [
                        { id: 'A', text: questionAnswers.a },
                        { id: 'B', text: questionAnswers.b },
                        { id: 'C', text: questionAnswers.c },
                        { id: 'D', text: questionAnswers.d }
                    ]
                };
                
                // Set the first question
                setQuestions([questionObj]);
                setCurrentQuestion(0);
                setCurrentIndex(result.data.current_index);
                
                // Reset timer to 45 seconds before starting
                setTimeLeft(45);
                setTimeExpired(false);
                
                // Close modal and start quiz
                setShowModal(false);
                setQuizStarted(true);
            } else if (!result.success) {
                // Handle error response
                setErrorMessage(result.message || 'Failed to start quiz. Please try again.');
                setShowErrorModal(true);
            }
        } catch (error) {
            console.error('Error starting quiz:', error);
        } finally {
            setStartingQuiz(false);
        }
    };

    const handleCloseModal = () => {
        navigate('/trivia-games');
    };

    // Show Finish button when current_index is 3 (last question)
    const isLastQuestion = currentIndex === 3;
    const currentQuestionData = questions[currentQuestion] || null;
    
    const handleCloseCompletionModal = () => {
        navigate('/trivia-games');
    };

    const handleTryAgain = () => {
        if (!canAccess) {
            navigate('/trivia-games');
            return;
        }
        
        // Reset quiz state
        setShowTimeoutModal(false);
        setTimeExpired(false);
        setQuestions([]);
        setCurrentQuestion(0);
        setCurrentIndex(0);
        setSelectedAnswer(null);
        setBatchId(null);
        setSubmittingAnswer(false);
        
        // Reset timer to 45 seconds
        setTimeLeft(45);
        
        // Show intro modal again
        setShowModal(true);
        setQuizStarted(false);
    };

    return (
         <div className='w-full flex items-start justify-center bg-[#F9FAFB] relative'>
            <div className='flex flex-col w-full '>
                <div>
                     <Header 
                     title='Symptom Sprint'
                     />  
                </div>

               <div 
                    className='min-h-screen p-4 bg-cover bg-center bg-no-repeat flex items-center justify-center'
                    style={{ backgroundImage: `url(${BackgroundImage})` }}
               >
                {showModal ? (
                    /* Intro Modal */
                    <div className='w-full max-w-xl px-4'>
                        <div className='bg-[#308042B2]/70 rounded-lg p-6 md:p-8 shadow-xl relative'>
                            {/* Close Button */}
                            <button
                                onClick={handleCloseModal}
                                className='absolute top-4 right-4 text-white hover:text-gray-300 transition-colors'
                            >
                                <IoClose className='text-2xl' />
                            </button>

                            {/* Title */}
                            <div className='text-center mb-6 mt-4'>
                                <h1 className='text-xl md:text-[32px] instrument-sans-bold text-white'>
                                    Symptom Sprint
                                </h1>
                            </div>

                            {/* Instructions */}
                            <div className='mb-6'>
                                <p className='text-white text-center text-sm md:text-base instrument-sans-normal'>
                                    Choose a list of symptoms and quickly select the most likely associated health condition.
                                    <br />
                                    <span className='font-bold mt-2 block'>You have 45 seconds to complete the quiz!</span>
                                </p>
                            </div>

                            {/* Attempts Note */}
                            {loading ? (
                                <div className='mb-8'>
                                    <div className='flex flex-col items-center gap-2'>
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                                        <p className='text-white text-center text-sm md:text-base instrument-sans-semibold'>
                                            Loading game configuration...
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className='mb-8'>
                                    <p className='text-white text-center text-sm md:text-base instrument-sans-semibold'>
                                        Note: You have {attemptsLeft} attempt{attemptsLeft !== 1 ? 's' : ''} left.
                                    </p>
                                </div>
                            )}

                            {/* Play Button */}
                            <div className='flex justify-center'>
                                <button
                                    onClick={handlePlay}
                                    disabled={!canAccess || loading || startingQuiz}
                                    className='bg-[#069125] hover:bg-white hover:text-black text-white py-3 px-12 rounded-lg transition-colors duration-200 instrument-sans-semibold w-full disabled:opacity-50 disabled:cursor-not-allowed'
                                >
                                    {startingQuiz ? 'Starting...' : loading ? 'Loading...' : 'Play'}
                                </button>
                            </div>

                            {showErrorModal && (
                                <div className='mt-4'>
                                    <p className='text-red-300 text-center text-sm md:text-base instrument-sans-normal'>
                                        {errorMessage}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                ) : !showCompletionModal && !showTimeoutModal ? (
                    /* Quiz Card */
                    currentQuestionData ? (
                        <div className='w-full max-w-xl px-4'>
                            <div className='bg-[#308042B2]/70 rounded-lg p-6 md:p-8 shadow-xl'>
                            {/* Timer and Question Progress */}
                            <div className='flex flex-col items-center mb-6'>
                                <div className='flex items-center gap-2 text-white mb-2'>
                                    <AiOutlineClockCircle className='text-xl' />
                                    <span className='text-base md:text-lg instrument-sans-bold'>
                                        {formatTime(timeLeft)}
                                    </span>
                                </div>
                                {batchId && (
                                    <p className='text-white/60 text-xs'>
                                        Batch ID: {batchId}
                                    </p>
                                )}
                            </div>

                            {/* Question Text */}
                            <div className='mb-8 flex flex-col items-center gap-2'>
                                <p className='text-base md:text-lg instrument-sans-bold text-white'>
                                    Question {currentQuestion + 1}/{totalQuestions || questions.length}
                                </p>

                                <h2 className='text-white text-lg md:text-[24px] text-center instrument-sans-bold'>
                                    {currentQuestionData.question}
                                </h2>
                            </div>

                            {/* Answer Options */}
                            <div className='space-y-4 mb-6'>
                                {currentQuestionData.options.map((option) => {
                                    const isSelected = selectedAnswer === option.id;
                                    
                                    return (
                                        <button
                                            key={option.id}
                                            onClick={() => handleAnswerSelect(option.id)}
                                            className={`w-full text-left p-4 rounded-lg border transition-all duration-200 ${
                                                isSelected
                                                    ? 'bg-white text-[#000] border-white'
                                                    : 'bg-transparent text-white border-[#ABBAAF] '
                                            }`}
                                        >
                                            <div className='flex items-center justify-between'>
                                                <span className='instrument-sans-medium'>
                                                    {option.id}. {option.text}
                                                </span>
                                                {isSelected && (
                                                    <FaCheckCircle className='text-[#069125] text-xl' />
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                        {/* Navigation Button */}
                        <div className='flex justify-end'>
                            {isLastQuestion ? (
                                <button
                                    onClick={handleFinish}
                                    disabled={!selectedAnswer || submittingAnswer}
                                    className='bg-[#069125] hover:bg-[#057a1f] text-white instrument-sans-medium py-3 px-8 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
                                >
                                    {submittingAnswer ? 'Submitting...' : 'Finish and Submit'}
                                </button>
                            ) : (
                                <button
                                    onClick={handleNext}
                                    disabled={!selectedAnswer || submittingAnswer}
                                    className='bg-[#069125] hover:bg-[#057a1f] text-white instrument-sans-medium py-3 px-8 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
                                >
                                    {submittingAnswer ? 'Loading...' : 'Next Question'}
                                </button>
                            )}
                        </div>
                        </div>
                        </div>
                    ) : (
                        <div className='w-full max-w-xl px-4'>
                            <div className='bg-[#308042B2]/70 rounded-lg p-6 md:p-8 shadow-xl text-center text-white'>
                                <p>Loading question...</p>
                            </div>
                        </div>
                    )
                ) : null}
               </div>
            </div>

            {/* Completion Modal */}
            {showCompletionModal && (
                <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
                    <div className='w-full max-w-xl px-4'>
                        <div className='bg-[#308042B2]/70 rounded-lg p-6 md:p-8 shadow-xl relative'>
                            {/* Title */}
                            <div className='text-center mb-6 mt-4'>
                                <h1 className='text-xl md:text-[32px] instrument-sans-bold text-white'>
                                    Quiz Completed!
                                </h1>
                            </div>

                            {/* Message */}
                            <div className='mb-8'>
                                <p className='text-white text-center text-sm md:text-base instrument-sans-normal'>
                                    {completionMessage}
                                </p>
                            </div>

                            {/* Back Button */}
                            <div className='flex justify-center'>
                                <button
                                    onClick={handleCloseCompletionModal}
                                    className='bg-[#069125] hover:bg-white hover:text-black text-white py-3 px-12 rounded-lg transition-colors duration-200 instrument-sans-semibold w-full'
                                >
                                    Back to Trivia Games
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Timeout Modal */}
            {showTimeoutModal && (
                <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
                    <div className='w-full max-w-xl px-4'>
                        <div className='bg-[#308042B2]/70 rounded-lg p-6 md:p-8 shadow-xl relative'>
                            {/* Title */}
                            <div className='text-center mb-6 mt-4'>
                                <h1 className='text-xl md:text-[32px] instrument-sans-bold text-white'>
                                    Time's Up!
                                </h1>
                            </div>

                            {/* Message */}
                            <div className='mb-8'>
                                <p className='text-white text-center text-sm md:text-base instrument-sans-normal'>
                                    Comiseration. you did not complete in time.
                                </p>
                            </div>

                            {/* Try Again Button */}
                            <div className='flex justify-center'>
                                <button
                                    onClick={handleTryAgain}
                                    disabled={!canAccess}
                                    className='bg-[#069125] hover:bg-white hover:text-black text-white py-3 px-12 rounded-lg transition-colors duration-200 instrument-sans-semibold w-full disabled:opacity-50 disabled:cursor-not-allowed'
                                >
                                    {canAccess ? 'Try Again' : 'Back to Trivia Games'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
       </div>
    );
};

export default SymptomSprint;